

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import { v4 as uuidv4 } from "uuid";
import { PDFDocument } from "pdf-lib";

export default function PublicTaxReturnUpload() {
  const { tokenId } = useParams();

  const apiBase = useMemo(() => {
    // Supports either REACT_APP_API_URL or a relative fallback.
    return process.env.REACT_APP_API_URL || "";
  }, []);

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [info, setInfo] = useState({ tax_year: null, checklist_name: "Tax Return" });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState({ ok: false, message: "" });

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoadingInfo(true);
      setResult({ ok: false, message: "" });

      try {
        const res = await fetch(`${apiBase}/api/public/tax-return/${tokenId}/`, { method: "GET" });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const msg = data.detail || "This upload link is invalid or expired.";
          if (isMounted) setResult({ ok: false, message: msg });
          if (isMounted) setLoadingInfo(false);
          return;
        }

        if (isMounted) {
          setInfo({
            tax_year: data.tax_year ?? null,
            checklist_name: data.checklist_name || "Tax Return",
          });
          setLoadingInfo(false);
        }
      } catch (e) {
        if (isMounted) {
          setResult({ ok: false, message: "Network error. Please refresh and try again." });
          setLoadingInfo(false);
        }
      }
    };

    if (tokenId) load();

    return () => {
      isMounted = false;
    };
  }, [apiBase, tokenId]);

  async function enhanceImage(file) {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");

    // Draw image
    ctx.drawImage(bitmap, 0, 0);

    // Get image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Convert to grayscale and boost contrast
    for (let i = 0; i < data.length; i += 4) {
      const grayscale = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const contrast = 1.5;
      const contrasted = (grayscale - 128) * contrast + 128;
      const final = Math.max(0, Math.min(255, contrasted));
      data[i] = data[i + 1] = data[i + 2] = final;
    }

    ctx.putImageData(imageData, 0, 0);

    // Export as compressed JPEG (high quality; we compress PDF later if needed)
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          const enhancedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
            type: "image/jpeg",
          });
          resolve(enhancedFile);
        },
        "image/jpeg",
        1
      );
    });
  }

  async function createMultiPagePDF(images, baseName) {
    const pageWidth = 612;
    const pageHeight = 792;
    const maxImageSize = 1800;
    const TEN_MB = 10 * 1024 * 1024;

    async function buildPdfWithQuality(quality) {
      const pdfDoc = new jsPDF({ unit: "pt", format: "letter" });
      for (let i = 0; i < images.length; i++) {
        const imgBitmap = await createImageBitmap(images[i]);

        const scale = Math.min(1, maxImageSize / Math.max(imgBitmap.width, imgBitmap.height));
        const resizedW = Math.floor(imgBitmap.width * scale);
        const resizedH = Math.floor(imgBitmap.height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = resizedW;
        canvas.height = resizedH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgBitmap, 0, 0, resizedW, resizedH);

        const dataUrl = canvas.toDataURL("image/jpeg", quality);

        const pdfScale = Math.min(pageWidth / resizedW, pageHeight / resizedH);
        const pdfW = resizedW * pdfScale;
        const pdfH = resizedH * pdfScale;
        const x = (pageWidth - pdfW) / 2;
        const y = (pageHeight - pdfH) / 2;

        if (i > 0) pdfDoc.addPage();
        pdfDoc.addImage(dataUrl, "JPEG", x, y, pdfW, pdfH);
      }
      return pdfDoc.output("blob");
    }

    let blob = await buildPdfWithQuality(0.95);
    if (blob.size > TEN_MB) {
      blob = await buildPdfWithQuality(0.8);
    }

    const safeName = (baseName || "tax-return")
      .split("?")[0]
      .replace(/^https?:\/\//, "")
      .replace(/[|\s\/]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 20);

    const uniqueId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || uuidv4();
    const fileName = `${safeName}-${uniqueId}.pdf`;
    return new File([blob], fileName, { type: "application/pdf" });
  }

  async function mergePDFs(files, baseName) {
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }
    const mergedPdfBytes = await mergedPdf.save();

    const safeName = (baseName || "tax-return")
      .split("?")[0]
      .replace(/^https?:\/\//, "")
      .replace(/[|\s\/]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 20);

    const uniqueId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || uuidv4();
    return new File([mergedPdfBytes], `${safeName}-${uniqueId}.pdf`, { type: "application/pdf" });
  }

  const handleSelectFiles = (event) => {
    setResult({ ok: false, message: "" });
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!selectedFiles.length) {
      setResult({ ok: false, message: "Please choose one or more files to upload." });
      return;
    }

    setSubmitting(true);
    setResult({ ok: false, message: "" });

    const images = selectedFiles.filter((f) => f.type && f.type.startsWith("image/"));
    const pdfs = selectedFiles.filter((f) => f.type === "application/pdf");

    const baseName = info.tax_year ? `${info.tax_year}-tax-return` : (info.checklist_name || "tax-return");

    try {
      // 1) Images => enhance + combine into one PDF
      if (images.length > 0) {
        const processedImages = await Promise.all(images.map(enhanceImage));
        const combinedPDF = await createMultiPagePDF(processedImages, baseName);
        const formData = new FormData();
        formData.append("file", combinedPDF);
        const res = await fetch(`${apiBase}/api/public/tax-return/${tokenId}/`, {
          method: "POST",
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || "Image upload failed.");
        }
      }

      // 2) PDFs => split encrypted vs plain
      if (pdfs.length > 0) {
        const encryptedFiles = [];
        const plainFiles = [];

        for (const file of pdfs) {
          try {
            await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: false });
            plainFiles.push(file);
          } catch (err) {
            encryptedFiles.push(file);
          }
        }

        // Merge and upload plain PDFs
        if (plainFiles.length > 0) {
          const mergedFile = await mergePDFs(plainFiles, baseName);
          const formData = new FormData();
          formData.append("file", mergedFile);
          const res = await fetch(`${apiBase}/api/public/tax-return/${tokenId}/`, {
            method: "POST",
            body: formData,
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.detail || "PDF upload failed.");
          }
        }

        // Upload encrypted PDFs as-is (renamed uniquely)
        for (const file of encryptedFiles) {
          const uniqueName = `${baseName}-${(window.crypto?.randomUUID && window.crypto.randomUUID()) || uuidv4()}.pdf`;
          const renamedFile = new File([file], uniqueName, { type: file.type });
          const formData = new FormData();
          formData.append("file", renamedFile);
          const res = await fetch(`${apiBase}/api/public/tax-return/${tokenId}/`, {
            method: "POST",
            body: formData,
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.detail || "Encrypted PDF upload failed.");
          }
        }
      }

      setSelectedFiles([]);
      setResetKey((prev) => prev + 1);
      setResult({ ok: true, message: "Upload received. Thank you." });
    } catch (err) {
      setResult({ ok: false, message: err?.message || "Upload failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const title = info.tax_year ? `${info.tax_year} Tax Return Upload` : "Tax Return Upload";

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 flex items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white rounded-2xl p-6 ring-1 ring-gray-200 shadow">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{title}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Use this page to upload your tax return securely without logging in.
            </p>
          </div>
        </div>

        {loadingInfo ? (
          <p className="mt-6 text-gray-700">Loading…</p>
        ) : result.message && !result.ok ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-red-700 font-medium">{result.message}</p>
            <p className="mt-2 text-sm text-gray-600">
              If you believe this is an error, contact our office.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Document:</span> {info.checklist_name}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Accepted file types: PDF or images. If multiple pages, please select them all and submit them all at once.
              </p>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Select file(s)</label>
                <input
                  key={resetKey}
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleSelectFiles}
                  className="mt-2 block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-500"
                />
                <p className="mt-2 text-xs text-gray-500">You can upload multiple images or PDFs at once.</p>

                {selectedFiles.length > 0 ? (
                  <ul className="mt-3 space-y-1 text-sm text-gray-700">
                    {selectedFiles.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-3">
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(idx)}
                          className="text-red-600 hover:underline text-xs shrink-0"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {result.message && result.ok ? (
                <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                  <p className="text-green-700 font-medium">{result.message}</p>
                  <p className="mt-2 text-sm text-gray-600">You can close this page.</p>
                </div>
              ) : null}

              {result.message && !result.ok ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="text-red-700 font-medium">{result.message}</p>
                </div>
              ) : null}

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting || selectedFiles.length === 0}
                  className={`inline-flex items-center justify-center rounded-xl px-4 py-2 font-semibold text-white focus:outline-none focus:ring-2 focus:ring-indigo-400/60 ${
                    submitting ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500"
                  }`}
                >
                  {submitting ? "Uploading…" : "Submit Documents"}
                </button>

                <div className="text-sm text-gray-600">
                  Not required to file? Use the <span className="font-medium">Non‑Filer</span> link from the email.
                  <span className="ml-2 text-gray-500">(If you have it, you can also go to </span>
                  <Link className="text-indigo-700 hover:underline" to={`/public/tax-return/${tokenId}/non-filer`}>
                    this page
                  </Link>
                  <span className="text-gray-500">.)</span>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Tip: If you have multiple pages as images, select them all and upload at once.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}